import React from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';
// components
import Modal from 'shared/modals/Modal';
import { FormikInput, FormikSelect } from 'shared/forms';
import FormikDatePicker from 'shared/forms/FormikDatePicker';
import toastr from 'shared/helpers/toastr';
import errorToastr from 'shared/helpers/errorToastr';
import LicenseEndorsementsGrid from './LicenseEndorsementsGrid';
// fetch
import { postDriverCertifications } from 'src/fetch/drivers';
// helpers
import { dateFormatter } from 'shared/../constants/Formatting';
import { getDateInputFormat } from 'shared/../helpers/date';

const DriverCertificationsModal = ({
  isOpen,
  onClose,
  initialValues,
  setFieldValue,
  setFieldTouched,
  handleSubmit,
  values,
  handleChange,
  handleBlur,
  touched,
  errors,
  certCategories,
  certTypes,
  states,
  restrictions,
  medicalRestrictions,
  isValid,
}) => {

  const typeOptions = [
    {
      label: 'Licenses',
      options: certCategories.licenses && certCategories.licenses.map(x => ({ label: x.description, value: x.value }))
    },
    {
      label: 'Certifications',
      options: certCategories.certifications && certCategories.certifications.map(x => ({ label: x.description, value: x.value }))
    },
    {
      label: 'Endorsements',
      options: certCategories.endorsements && certCategories.endorsements.map(x => ({ label: x.description, value: x.value }))
    },
  ];

  const getTypeValue = () => {
    if (values.type || values.type === 0) {
      const cert = certTypes.find(c => c.value === values.type);
      return { label: cert.description, value: cert.value };
    }
  };

  const handleClose = () => onClose();

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} containerStyle='medium'>
      <div className='modal-header'>
        <h5 className='modal-title' id='add-reason-modal'>
          {initialValues && initialValues.id ? 'Edit' : 'Add'} License, Certification or Endorsement
        </h5>
        <button type="button" className='button large close' onClick={handleClose} />
      </div>
      <section className='cert-form'>
        <form onSubmit={handleSubmit}>
          <section className='modal-body formik-form rows'>
            <div className='formik-row'>
              <FormikSelect
                onChange={selected => setFieldValue('type', selected.value)}
                wrapperClass='wrapper-fill-column'
                label='Type'
                overflowModal
                options={typeOptions}
                name='type'
                value={getTypeValue()}
                onBlur={setFieldTouched}
                touched={touched}
                errors={errors}
              />
            </div>
            <div className='formik-row'>
              <FormikInput
                wrapperClass='wrapper-two-column'
                type='text'
                label='Number'
                name='number'
                value={values.number}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
              <FormikSelect
                onChange={selected => setFieldValue('state', selected.value)}
                wrapperClass='wrapper-two-column'
                label='State'
                overflowModal
                options={states.map(x => ({ label: x.text, value: x.value }))}
                name='state'
                value={values.state}
                onBlur={setFieldTouched}
                touched={touched}
                errors={errors}
              />
            </div>
            <div className='formik-row'>
              <FormikDatePicker
                wrapperClass='wrapper-two-column'
                label='Effective Date'
                name='effectiveDate'
                value={values.effectiveDate}
                onChange={val => setFieldValue('effectiveDate', val)}
                onBlur={e => handleBlur(e)}
                errors={errors}
                setTouched={name => setFieldTouched(name, true)}
              />
              <FormikDatePicker
                wrapperClass='wrapper-two-column'
                label='Expiration Date'
                name='expirationDate'
                value={values.expirationDate}
                onChange={val => setFieldValue('expirationDate', val)}
                onBlur={e => handleBlur(e)}
                errors={errors}
                setTouched={name => setFieldTouched(name, true)}
              />
            </div>
            { //License
              values.type === 0 && (
              <div>
                <div className='formik-row'>
                  <FormikSelect
                    onChange={selected => {
                      const newOptions = selected?.map(x => x.value) ?? [];
                      setFieldValue('licenseRestrictions', newOptions);
                    }
                    }
                    isMulti
                    wrapperClass='wrapper-one-column'
                    label='Restrictions'
                    useCommaSeparatedValues
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    options={restrictions.map(x => ({ label: x.description, value: x.value }))}
                    name='restrictions'
                    value={values.licenseRestrictions?.map(value => {
                      const selected = restrictions?.find(x => x.value === value);
                      return { value, label: selected?.description };
                    })}
                    overflowModal
                    onBlur={setFieldTouched}
                    touched={touched}
                    errors={errors}
                  />
                </div>
                <div className='formik-row'>
                  <LicenseEndorsementsGrid />
                </div>
              </div>
              )}
              { //Medical
              values.type === 1 && (<div className='formik-row' style={{ marginBottom: '13rem' }}>
                <FormikSelect
                  onChange={selected => {
                    const newOptions = selected?.map(x => x.value) ?? [];
                    setFieldValue('medicalCardRestrictions', newOptions);
                  }
                  }
                  isMulti
                  wrapperClass='wrapper-one-column'
                  label='Restrictions'
                  useCommaSeparatedValues
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  options={medicalRestrictions.map(x => ({ label: x.description, value: x.value }))}
                  name='medicalCardRestrictions'
                  value={values.medicalCardRestrictions?.map(value => {
                    const selected = medicalRestrictions?.find(x => x.value === value);
                    return { value, label: selected?.description };
                  })}
                  overflowModal
                  onBlur={setFieldTouched}
                  touched={touched}
                  errors={errors}
                />
              </div>)}
          </section>
          <section className='modal-footer toolbar toolbar--xxs'>
            <div className='toolbar--column toolbar--right'>
              <button
                type='submit'
                className='button large secondary text-align--center toolbar--button--margin'
                disabled={!isValid}
              >
                Submit
              </button>
              <button
                type="button"
                id='add-reason-cancel'
                className='button large white text-align--center margin--none--xxs'
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
          </section>
        </form>
      </section>
    </Modal>
  );
};

const CertsSchema = Yup.object().shape({
  type: Yup.number().required('Item Type Is Required').typeError('Item Type Is Required'),
  expirationDate: Yup.string().required('Expiration Date Is Required').typeError('Expiration Date Is Required'),
  licenseEndorsements: Yup.array().of(
    Yup.object().shape({
      typeId: Yup.number().nullable().required('Type is Required'),
      expirationDate: Yup.string().nullable().required('Expiration Date is Required'),
    }),
  ),
});

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues = {}, driverId }) => {
    const {
      effectiveDate,
      expirationDate,
      licenseEndorsements,
      ...rest
    } = initialValues;
    return {
      type: '',
      licenseRestrictions: [],
      medicalCardRestrictions: [],
      driverId: driverId,
      effectiveDate: effectiveDate ? dateFormatter(effectiveDate) : null,
      expirationDate: expirationDate ? dateFormatter(expirationDate) : null,
      licenseEndorsements: licenseEndorsements ? licenseEndorsements.map(
        ({ expirationDate, ...rest }) => ({
          expirationDate: expirationDate ? dateFormatter(expirationDate) : null,
          ...rest
        }),
      ) : [],
      ...rest ?? {},
    };
  },
  validationSchema: CertsSchema,
  handleSubmit: async (values, { setSubmitting, props: { onClose } }) => {
    setSubmitting(true);

    const data = {
      ...values,
      effectiveDate: getDateInputFormat(values.effectiveDate),
      expirationDate: getDateInputFormat(values.expirationDate),
      licenseEndorsements: values.licenseEndorsements.map(
        ({ expirationDate, ...rest }) => ({
          expirationDate: getDateInputFormat(expirationDate),
          ...rest,
        }),
      ),
    };

    try {
      await postDriverCertifications(data);
      setSubmitting(false);
      toastr('success', 'Item Saved Successfully.');
      onClose(true);
    } catch (error) {
      errorToastr({
        error,
        messagePrefix: 'Problem saving driver.'
      });
      setSubmitting(false);
    }
  }
})(DriverCertificationsModal);
